import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { login } from '../redux/authSlice';
import { AppDispatch } from '../redux/store';
import { authApi, LoginCredentials, ApiError } from '../services/api';
import { X, Mail, Lock, Eye, EyeOff, AlertCircle, LogIn } from 'lucide-react';

interface LoginModalProps {
  onClose: () => void;
  onSwitchToSignup: () => void;
  onForgotPassword: () => void;
}

interface FormErrors {
  email?: string;
  password?: string;
  twoFactorCode?: string;
  general?: string;
}

const LoginModal: React.FC<LoginModalProps> = ({ onClose, onSwitchToSignup, onForgotPassword }) => {
  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: '',
    password: '',
  });
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorMode, setTwoFactorMode] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleBlur = (fieldName: string) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
    validateField(fieldName);
  };

  const validateField = (fieldName: string): boolean => {
    let isValid = true;
    const newErrors = { ...errors };

    switch (fieldName) {
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!credentials.email) {
          newErrors.email = 'Email is required';
          isValid = false;
        } else if (!emailRegex.test(credentials.email)) {
          newErrors.email = 'Please enter a valid email address';
          isValid = false;
        } else {
          delete newErrors.email;
        }
        break;
      case 'password':
        if (!credentials.password) {
          newErrors.password = 'Password is required';
          isValid = false;
        } else {
          delete newErrors.password;
        }
        break;
      case 'twoFactorCode':
        if (!twoFactorCode) {
          newErrors.twoFactorCode = '2FA code is required';
          isValid = false;
        } else if (!/^\d{6}$/.test(twoFactorCode)) {
          newErrors.twoFactorCode = '2FA code must be 6 digits';
          isValid = false;
        } else {
          delete newErrors.twoFactorCode;
        }
        break;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateForm = (): boolean => {
    const fields = twoFactorMode ? ['twoFactorCode'] : ['email', 'password'];
    let isValid = true;
    fields.forEach((field) => {
      if (!validateField(field)) isValid = false;
    });
    setTouched(fields.reduce((acc, field) => ({ ...acc, [field]: true }), {} as Record<string, boolean>));
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      if (twoFactorMode) {
        const response = await authApi.verifyTwoFactor({
          email: loginEmail,
          twoFactorCode
        });

        const user = response.data.user;
        if (response.success && response.data.tokens && user) {
          sessionStorage.setItem('token', response.data.tokens.accessToken);
          dispatch(login({ user, token: response.data.tokens.accessToken }));

          if (user.role === 'admin') {
            navigate('/admin-dashboard');
          } else {
            navigate('/user-dashboard');
          }
          onClose();
          return;
        }

        setErrors({ general: response.message || 'Unable to complete sign in with 2FA.' });
        return;
      }

      const isAdminLogin =
        credentials.email.includes('admin') ||
        credentials.email === process.env.REACT_APP_ADMIN_EMAIL;

      const response = isAdminLogin
        ? await authApi.adminLogin(credentials)
        : await authApi.login(credentials);

      if (response.success) {
        if (response.data.twoFactorRequired) {
          setTwoFactorMode(true);
          setLoginEmail(credentials.email);
          setInfoMessage('Enter the 6-digit code from your authenticator app to complete sign in.');
          return;
        }

        if (response.data.tokens && response.data.user) {
          sessionStorage.setItem('token', response.data.tokens.accessToken);
          dispatch(login({ user: response.data.user, token: response.data.tokens.accessToken }));

          if (response.data.user.role === 'admin') {
            navigate('/admin-dashboard');
          } else {
            navigate('/user-dashboard');
          }
          onClose();
          return;
        }

        setErrors({ general: response.message || 'Login failed. Please check your credentials.' });
      } else {
        setErrors({ general: response.message || 'Login failed. Please check your credentials.' });
      }
    } catch (error) {
      const apiError = error as { response?: { data?: ApiError } };
      if (apiError.response?.data) {
        setErrors({ general: apiError.response.data.message || 'Login failed. Please check your credentials.' });
      } else {
        setErrors({ general: 'An unexpected error occurred. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) return null;

  return createPortal(
    <div
      className="modal-backdrop"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="relative bg-gradient-to-br from-fintech-primary to-blue-800 px-8 pt-8 pb-10">
          <button
            className="absolute top-4 right-4 p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center mb-4">
            <LogIn size={22} className="text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-1">Welcome back</h2>
          <p className="text-blue-200 text-sm">Sign in to your Kenluk Pay account</p>
        </div>

        {/* Form */}
        <div className="px-8 py-7">
          {errors.general && (
            <div className="alert alert-error mb-5">
              <AlertCircle size={16} className="flex-shrink-0" />
              <span>{errors.general}</span>
            </div>
          )}

          {infoMessage && (
            <div className="alert alert-info mb-5">
              <span>{infoMessage}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            {!twoFactorMode ? (
              <>
                {/* Email */}
                <div className="form-group mb-0">
                  <label className="form-label" htmlFor="login-email">Email Address</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-fintech-textMuted pointer-events-none" />
                    <input
                      type="email"
                      id="login-email"
                      name="email"
                      value={credentials.email}
                      onChange={handleChange}
                      onBlur={() => handleBlur('email')}
                      className={`form-input !pl-10 ${touched.email && errors.email ? 'error' : ''}`}
                      placeholder="you@company.com"
                      required
                    />
                  </div>
                  {touched.email && errors.email && (
                    <p className="form-error">
                      <AlertCircle size={12} />
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div className="form-group mb-0">
                  <label className="form-label" htmlFor="login-password">Password</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-fintech-textMuted pointer-events-none" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="login-password"
                      name="password"
                      value={credentials.password}
                      onChange={handleChange}
                      onBlur={() => handleBlur('password')}
                      className={`form-input !pl-10 !pr-10 ${touched.password && errors.password ? 'error' : ''}`}
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-fintech-textMuted hover:text-fintech-text transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {touched.password && errors.password && (
                    <p className="form-error">
                      <AlertCircle size={12} />
                      {errors.password}
                    </p>
                  )}
                </div>

                {/* Forgot password */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="link-btn text-sm"
                    onClick={onForgotPassword}
                  >
                    Forgot password?
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="form-group mb-0">
                  <label className="form-label" htmlFor="two-factor-code">2FA Code</label>
                  <div className="relative">
                    <input
                      type="text"
                      id="two-factor-code"
                      name="twoFactorCode"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value)}
                      onBlur={() => handleBlur('twoFactorCode')}
                      className={`form-input ${touched.twoFactorCode && errors.twoFactorCode ? 'error' : ''}`}
                      placeholder="Enter 6-digit code"
                      maxLength={6}
                      required
                    />
                  </div>
                  {touched.twoFactorCode && errors.twoFactorCode && (
                    <p className="form-error">
                      <AlertCircle size={12} />
                      {errors.twoFactorCode}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-3">
                  <button
                    type="button"
                    className="link-btn text-sm self-start"
                    onClick={() => {
                      setTwoFactorMode(false);
                      setInfoMessage(null);
                      setTwoFactorCode('');
                      setTouched({});
                    }}
                  >
                    Back to password login
                  </button>
                </div>
              </>
            )}

            {/* Submit */}
            <button
              type="submit"
              className="btn-primary w-full justify-center py-3"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner w-4 h-4" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn size={16} />
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="divider mt-6">
            <span>New to Kenluk Pay?</span>
          </div>
          <button
            type="button"
            className="btn-secondary w-full justify-center"
            onClick={onSwitchToSignup}
          >
            Create an Account
          </button>
        </div>
      </div>
    </div>,
    modalRoot
  );
};

export default LoginModal;
